import { useEffect, useState } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import maxBy from 'lodash/maxBy'
import { ResponseVariant } from 'workflow/Insights/Insights'
import helpers from 'workflow/utils/helpers'

import IconTitleGroup from 'common/components/iconTitleGroup'
import InfoAction from 'common/components/InfoAction'
import Loader from 'common/components/loaders/Loader'
import ProductCard from 'common/components/productCard'
import Tags from 'common/components/tags'
import { capitalizeFirstLetter } from 'common/helpers/string'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import useSelectedComponentElement from 'common/hooks/useSelectedComponentElement'
import useGetCategoryDetailsQuery from 'features/unifiedFlow/api/queries/useGetCategoryDetailsQuery'
import useGetProductsQuery from 'features/unifiedFlow/api/queries/useGetProductsQuery'
import useGetVariableCombinationQuery from 'features/unifiedFlow/api/queries/useGetVariableCombinationsQuery'
import SentimentAnalysisChart from 'features/unifiedFlow/contentPage/generate/rightSection/sentimentAnalysis/SentimentAnalysisChart'
import { hideRightPanel } from 'features/unifiedFlow/store/unifiedFlowSlice'

import RightPanel from '../../components/layout/RightPanel'
import RightPanelHeading from '../../components/layout/RightPanelHeading'
import InsightsCard from '../../generate/rightSection/InsightsCard'
import SentimentDescription from '../../generate/rightSection/SentimentDescription'
import { getIsPersonalized } from '../../helpers'
import { useVariantLanguageContext } from '../../review/context/VariantLanguageContext'

import CollapsedIcons from './CollapsedIcons'
import ExpandedIcons from './ExpandedIcons'

type Props = {
  selectedVariant: ResponseVariant | undefined
  statusFilter: any
  hasLoadingVariants: boolean
  onInputResultsClick: () => void
}

const RightSection = ({
  selectedVariant,
  statusFilter,
  hasLoadingVariants,
  onInputResultsClick,
}: Props) => {
  const { showUfManualResults } = useFlags()
  const dispatch = useAppDispatch()
  const campaign = useAppSelector((state) => state.campaignStates.campaignData)
  const isBandit = helpers.isBanditCampaign(campaign?.campaign_configuration)
  const showInputResultsButton =
    showUfManualResults &&
    (!isBandit ||
      helpers.isBanditManualConversionsCampaign(
        campaign?.campaign_configuration
      )) &&
    campaign?.campaign_progress?.text_variants_accepted
  const {
    state: { selectedVariantCombinationId, selectedVariantId },
  } = useVariantLanguageContext()

  const { data: selectedElement } = useSelectedComponentElement()

  const selectedPersonalizedVariant =
    selectedElement?.personalized_variants?.find(
      (personalizedVariant) =>
        personalizedVariant.variant_id === selectedVariantId
    )

  const personalizationId = selectedElement?.personalization_id
  const region =
    selectedElement?.personalization_settings?.configuration.privacy_region
  const catalogId =
    selectedElement?.personalization_settings?.configuration
      .product_catalogue_id

  const { data: variableCombinations, ...variableCombinationQuery } =
    useGetVariableCombinationQuery({
      personalizationId: personalizationId ?? '',
      region,
    })

  const currentCombination = variableCombinations?.items.find(
    (combination) => combination.id === selectedVariantCombinationId
  )

  const rawSentiments =
    selectedVariant?.sentiments ?? selectedPersonalizedVariant?.sentiments ?? {}

  const mappedSentiments = Object.entries(rawSentiments).map(
    ([name, sentimentData]) => ({
      name: name,
      value:
        typeof sentimentData === 'object'
          ? ((sentimentData as { score?: number }).score ?? 0)
          : 0,
    })
  )
  const defaultSentimentName = maxBy(mappedSentiments, 'value')?.name
  const sentiments: Array<{ name: string; value: number }> = mappedSentiments

  const [selectedSentiment, setSelectedSentiment] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    setSelectedSentiment(defaultSentimentName)
  }, [defaultSentimentName])

  useEffect(() => {
    if (!selectedElement) {
      dispatch(hideRightPanel({ visibility: 'none' }))
    } else {
      dispatch(hideRightPanel({ visibility: 'collapsed' }))
    }
  }, [dispatch, selectedElement])

  const attributeTags: { label: string; value: string }[] =
    currentCombination?.dimensions.map(({ key, value }) => ({
      label: `${capitalizeFirstLetter(key)} : ${value}`,
      value,
    })) ?? []

  const categoryId = currentCombination?.productCategoryId
  const productId = currentCombination?.productId
  const isProductDataAvailable = !!catalogId && !!categoryId

  const { data: products, ...productsQuery } = useGetProductsQuery({
    catalogId,
    categoryId,
    region,
  })

  const { data: productCategoryDetails, ...categoryDetailsQuery } =
    useGetCategoryDetailsQuery({
      catalogId,
      categoryId,
      region,
    })

  const currentProduct = products?.find((product) => product.id === productId)
  const productDescription = currentProduct?.description
  const productName = currentProduct?.name
  const categoryPath = productCategoryDetails?.ccn_path ?? []

  const isLoading =
    productsQuery.isFetching ||
    categoryDetailsQuery.isFetching ||
    variableCombinationQuery.isFetching

  const isError =
    productsQuery.isError ||
    categoryDetailsQuery.isError ||
    variableCombinationQuery.isError

  const isPersonalizeCampaign = getIsPersonalized(selectedElement)

  return (
    <RightPanel
      collapsedIcons={
        <CollapsedIcons
          statusFilter={statusFilter}
          hasLoadingVariants={hasLoadingVariants}
          showInputResultsButton={showInputResultsButton}
          onInputResultsClick={onInputResultsClick}
        />
      }
      onOutsideClick={() => {
        dispatch(hideRightPanel())
      }}
    >
      <Loader isLoading={isLoading}>
        {isPersonalizeCampaign && (
          <div className="flex flex-col gap-6 p-6">
            <RightPanelHeading
              text="Language context"
              icons={
                <ExpandedIcons
                  statusFilter={statusFilter}
                  hasLoadingVariants={hasLoadingVariants}
                  showInputResultsButton={showInputResultsButton}
                  onInputResultsClick={onInputResultsClick}
                />
              }
            />
            <div>
              <div className="flex justify-between mb-2">
                <IconTitleGroup title="Item and Category" />
                <InfoAction
                  message="Contextual attributes, based on which the variant was generated"
                  messagePlacement="left"
                />
              </div>
              {attributeTags.length === 0 || isError ? (
                <div>No contextual variables found for this variant</div>
              ) : (
                <Tags isFreeText tags={attributeTags} />
              )}
            </div>
            <div>
              <IconTitleGroup title="Item description" className="mb-2" />
              {isProductDataAvailable || isError ? (
                <ProductCard
                  id={productId ?? ''}
                  name={productName ?? ''}
                  categoryPath={categoryPath}
                  description={productDescription}
                />
              ) : (
                <div>Item data is not available</div>
              )}
            </div>
          </div>
        )}
        {selectedSentiment && sentiments && sentiments.length > 0 && (
          <>
            {isPersonalizeCampaign && (
              <hr className="my-2 mx-6 border-gold-400" />
            )}
            <div className="p-6 pt-0">
              <InsightsCard
                title="Sentiment map"
                hasTitleIcons
                content={
                  <SentimentAnalysisChart
                    isLoading={false}
                    selectedSentiment={selectedSentiment}
                    series={sentiments}
                    onClickSentiment={({ name }) => setSelectedSentiment(name)}
                  />
                }
              />
            </div>
            <div className="sticky top-0 z-10 bg-gold-100 pt-6 pb-4 px-6">
              <SentimentDescription sentiment={selectedSentiment} />
            </div>
          </>
        )}
      </Loader>
    </RightPanel>
  )
}

export default RightSection
